import { memo, startTransition, useRef, useState } from "react"
import type {
    BaseSyntheticEvent,
    Dispatch,
    KeyboardEvent,
    MouseEvent,
    NamedExoticComponent,
    ReactElement,
    RefObject,
    SetStateAction
} from "react"

import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"
import { createPortal } from "react-dom"
import { useBoolean, useClickAway, useNumber, useUpdateEffect } from "react-use"
import type { CounterActions } from "react-use/lib/useCounter"

import { ArrowIcon } from "@/3514/assets"
import { type TUseInteractionState, useInteractionState } from "@/3514/hooks"
import { THEME } from "@/3514/theme"
import { ERoundDirection, buildCssClassName, getTransitionTwClasses, propsAreEqual } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { ESpinnerSize, Spinner } from "../../spinner"
import { ETypographySize, ETypographyTag, Typography } from "../../typography"

import assets from "./select.assets"
import config from "./select.config"
import helpers from "./select.helpers"
import { ESelectSize, ESelectVariant, type ISelectOption, type TSelectProps as TProps } from "./select.types"

const Component: NamedExoticComponent<TProps> = memo(
    ({
        variant = ESelectVariant.Default,
        size = ESelectSize.Medium,
        isDisabled = false,
        isLoading = false,
        isError = false,
        label = String(),
        defaultValue = String(),
        options,
        onChange,
        placeholder = "– –",
        id = undefined,
        onOpenChange = undefined,
        wrapperClassName = String(),
        withFixedContainer = undefined,
        maxHeight = 200
    }: TProps): ReactElement => {
        const { t }: IUseTranslation = useTranslation()

        const [isSelectOpen, setIsSelectOpen]: ReturnType<typeof useBoolean> = useBoolean(false)

        const [currentOptionIndex, { set: setCurrentOptionIndex }]: [number, CounterActions] = useNumber(0)

        const {
            isActionAffected: isContainerActionAffected,
            actions: {
                onMouseEnter: onContainerMouseEnter,
                onMouseLeave: onContainerMouseLeave,
                onFocus: onContainerFocus,
                onBlur: onContainerBlur
            }
        }: TUseInteractionState = useInteractionState()

        const [selectedOption, setSelectedOption]: [ISelectOption, Dispatch<SetStateAction<ISelectOption>>] =
            useState<ISelectOption>(
                isLoading
                    ? { label: String(""), value: t("common.loading"), id: String(), isDisabled }
                    : {
                          label: String(defaultValue || placeholder),
                          value: String(defaultValue || placeholder),
                          id: String(defaultValue || ""),
                          isDisabled
                      }
            )

        const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
        const wrapperRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
        const optionsListRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

        function handleClickOutSide(e: UIEvent): void {
            startTransition((): void => {
                if (!wrapperRef?.current.contains(e.target as Node)) {
                    e.stopPropagation()
                    setIsSelectOpen(false)
                }
            })
        }

        useClickAway(optionsListRef, handleClickOutSide)

        useUpdateEffect((): void => {
            onOpenChange?.(id, isSelectOpen)
        }, [isSelectOpen, onOpenChange, id])

        function handleOptionAction(e: KeyboardEvent & MouseEvent, { index }: { index: number }): void {
            startTransition((): void => {
                if (eq(e.key, "Enter") || eq(e.type, "click")) {
                    if (options[index].isDisabled) return
                    setSelectedOption(options[index])
                    setCurrentOptionIndex(index)
                    setIsSelectOpen(false)
                    onChange(options[index].id)
                }
            })
        }

        const handleSelectAction: (e: BaseSyntheticEvent) => void = (e: KeyboardEvent & MouseEvent): void => {
            startTransition((): void => {
                if (!isSelectOpen && (eq(e.key, "Enter") || eq(e.type, "click"))) {
                    if (!isDisabled) setIsSelectOpen((prev: boolean): boolean => !prev)
                } else if (isSelectOpen) {
                    if (eq(e.key, "Enter"))
                        handleOptionAction(e, {
                            index: !isEmpty((e.target as HTMLElement).dataset.index)
                                ? Number((e.target as HTMLElement).dataset.index)
                                : currentOptionIndex
                        })
                    if (eq(e.type, "click")) {
                        if (!isEmpty((e.target as HTMLElement).dataset.index)) {
                            handleOptionAction(e, { index: Number((e.target as HTMLElement).dataset.index) })
                        } else {
                            setIsSelectOpen(false)
                        }
                    }

                    if (eq(e.key, "Tab")) {
                        e.stopPropagation()
                        e.preventDefault()
                        if (e.shiftKey) {
                            setCurrentOptionIndex((prev: number): number =>
                                eq(prev - 1, 0) || eq(prev, 0) ? 0 : prev - 1
                            )
                        } else {
                            setCurrentOptionIndex((prev: number): number =>
                                eq(prev + 1, options?.length) ? prev : prev + 1
                            )
                        }
                    } else {
                        switch (e.key) {
                            case "Escape":
                                e.stopPropagation()
                                e.preventDefault()
                                setIsSelectOpen(false)
                                break
                            case "ArrowDown":
                                e.stopPropagation()
                                e.preventDefault()
                                setCurrentOptionIndex((prev: number): number =>
                                    eq(prev + 1, options?.length) ? prev : prev + 1
                                )
                                break
                            case "ArrowUp":
                                e.stopPropagation()
                                e.preventDefault()
                                setCurrentOptionIndex((prev: number): number =>
                                    eq(prev - 1, 0) || eq(prev, 0) ? 0 : prev - 1
                                )
                                break
                            default:
                                break
                        }
                    }
                }
            })
        }

        function renderContainer(): ReactElement {
            return (
                <div
                    role="listbox"
                    tabIndex={-1}
                    onClick={handleSelectAction}
                    onKeyDown={handleSelectAction}
                    ref={optionsListRef}
                    style={{
                        maxWidth: `${containerRef.current?.clientWidth}px`,
                        ...(withFixedContainer && {
                            maxHeight: `${maxHeight}px`,
                            top: `${containerRef.current.getBoundingClientRect().bottom}px`,
                            left: `${containerRef.current?.getBoundingClientRect().left}px`
                        })
                    }}
                    className={buildCssClassName({
                        [config.variantClassesMap[variant].container({
                            isFixedPosition: withFixedContainer,
                            isError
                        })]: variant,
                        [config.sizeClassesMap[size].container()]: size
                    })}
                >
                    {isLoading ? (
                        <Spinner size={ESpinnerSize.Small} className={config.baseClasses.spinner()} />
                    ) : isEmpty(options) ? (
                        <Typography
                            tag={ETypographyTag.Paragraph}
                            size={ETypographySize.Small}
                            text="No options available."
                            className={config.baseClasses.emptyOptionsText()}
                        />
                    ) : (
                        map<ISelectOption, ReactElement>(
                            options,
                            (option: ISelectOption, index: number): ReactElement => {
                                const _isCurrent: boolean = eq(currentOptionIndex, index)

                                return (
                                    <div
                                        key={option.id}
                                        id={option.id}
                                        tabIndex={option.isDisabled ? -1 : 0}
                                        role="option"
                                        data-index={index}
                                        aria-selected={eq(selectedOption.value, option.value)}
                                        aria-disabled={option.isDisabled}
                                        className={buildCssClassName({
                                            [config.variantClassesMap[variant].option({
                                                wasSelected: eq(selectedOption.id, option.id),
                                                isActive: _isCurrent,
                                                isDisabled: option.isDisabled
                                            })]: variant
                                        })}
                                    >
                                        {helpers.renderOption({
                                            option,
                                            isCurrent: _isCurrent,
                                            isError: false,
                                            isItalic: false
                                        })}
                                    </div>
                                )
                            }
                        )
                    )}
                </div>
            )
        }

        return (
            <div
                ref={containerRef}
                className={buildCssClassName(config.baseClasses.container({ size }), {
                    [wrapperClassName]: wrapperClassName
                })}
            >
                {helpers.renderLabel({ label })}

                <div
                    className={buildCssClassName(config.baseClasses.wrapper(), {
                        [config.sizeClassesMap[size].wrapper()]: size
                    })}
                >
                    <div
                        role="button"
                        aria-haspopup="listbox"
                        aria-expanded={isSelectOpen}
                        aria-disabled={isDisabled}
                        aria-label="Open select"
                        tabIndex={isDisabled ? -1 : 0}
                        ref={wrapperRef}
                        className={buildCssClassName({
                            [config.variantClassesMap[variant].wrapper({
                                isOpen: isSelectOpen,
                                isError,
                                isDisabled
                            })]: variant
                        })}
                        {...(!isDisabled &&
                            !isLoading && {
                                onClick: handleSelectAction,
                                onKeyDown: handleSelectAction,
                                onMouseEnter: onContainerMouseEnter,
                                onMouseLeave: onContainerMouseLeave,
                                onFocus: onContainerFocus,
                                onBlur: onContainerBlur
                            })}
                    >
                        {helpers.renderOption({
                            option: selectedOption,
                            isCurrent: false,
                            isError,
                            isItalic: placeholder !== "– –" && !selectedOption.id
                        })}

                        {isDisabled ? (
                            <assets.LockIcon />
                        ) : (
                            <ArrowIcon
                                width={14}
                                height={14}
                                direction={ERoundDirection.Bottom}
                                className={getTransitionTwClasses("all", 200)}
                                fillColor={((): string => {
                                    if (isError) return THEME.COLORS.red[100]
                                    if (isSelectOpen || isContainerActionAffected) return THEME.COLORS.accent

                                    return THEME.COLORS.blue[200]
                                })()}
                            />
                        )}
                    </div>

                    {isSelectOpen
                        ? withFixedContainer
                            ? createPortal(renderContainer(), document.body)
                            : renderContainer()
                        : null}
                </div>
            </div>
        )
    },
    propsAreEqual<TProps>
)

Component.displayName = "Select"

export { Component as Select }
